import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { OpfoelgningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/opfoelgning-dto';
import { ServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-dto';
import { OpkraevningsIntervalDialogComponent } from 'src/app/components/aftale/create/forms/abonnement-form/opkraevnings-interval-dialog/opkraevnings-interval-dialog.component';
import { OpfoelgningAction, OpfoelgningDialogData, OpfoelgningDialogResponse } from 'src/app/components/aftale/models';
import { ParkerInfoDialogComponent } from 'src/app/components/opfoelgning/parker-info-dialog/parker-info-dialog.component';
import { AuthService } from 'src/app/services/auth.service';
import { ServiceordreStatusEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceordre-status-enum';

@Component({
    selector: 'app-opfoelgning-dialog',
    templateUrl: './opfoelgning-dialog.component.html',
    styleUrls: ['./opfoelgning-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OpfoelgningDialogComponent implements OnInit {
  form: UntypedFormGroup;

  serviceordreStatusEnum = ServiceordreStatusEnum;

  opfoelgning: OpfoelgningDto;
  serviceordre: ServiceordreDto;
  userId: string;
  get canSave(): boolean {
    return !this.opfoelgning?.id || this.userId == this.opfoelgning?.user;
  }
  get canRemove(): boolean {
    return this.opfoelgning?.id && this.userId == this.opfoelgning?.user;
  }

  unsubscribe$ = new Subject<void>();
  opfoelgninger: OpfoelgningDto[];
  isParked: ServiceordreStatusEnum = ServiceordreStatusEnum.Parked;

  get note() {
    return this.form.get('note');
  }

  get deadline() {
    return this.form.get('deadline');
  }

  get prioritet() {
    return this.form.get('prioritet');
  }

  get parker() {
    return this.form.get('parker');
  }

  get title(): string {
    return !!this.serviceordre ? 'ordre' : 'aftale';
  }

  constructor(
    private fb: UntypedFormBuilder,
    private auth: AuthService,
    private infoDialog: MatDialog,
    private dialog: MatDialogRef<OpkraevningsIntervalDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OpfoelgningDialogData
  ) {
    this.serviceordre = data.serviceordre;
    this.userId = this.auth.username;
    this.opfoelgninger = data.opfoelgninger;
  }

  ngOnInit(): void {
    if (!this.opfoelgning && !this.opfoelgninger?.length) this.opfoelgning = {};
    if (this.opfoelgning) this.init();
  }

  private init() {
    this.form = this.fb.group({
      prioritet: new UntypedFormControl({ value: this.opfoelgning?.priority, disabled: !this.canSave }, Validators.required),
      note: new UntypedFormControl({ value: this.opfoelgning?.note, disabled: !this.canSave }, Validators.maxLength(500)),
      deadline: new UntypedFormControl({ value: this.opfoelgning?.deadline, disabled: !this.canSave }),
      parker: new UntypedFormControl({
        value: this.serviceordre?.serviceordreStateEnum == ServiceordreStatusEnum.Parked,
        disabled: !this.canSave,
      }),
    });

    if (this.serviceordre?.serviceordreStateEnum == ServiceordreStatusEnum.Parked) {
      this.note.setValidators([Validators.required]);
      this.note.updateValueAndValidity();
    }

    this.parker.valueChanges.pipe(takeUntil(this.unsubscribe$), debounceTime(100), distinctUntilChanged()).subscribe((value) => {
      if (value) {
        this.note.setValidators([Validators.required]);
      } else {
        this.note.clearValidators();
      }
      this.note.updateValueAndValidity();
    });
  }

  save() {
    const response: OpfoelgningDialogResponse = {
      action: OpfoelgningAction.Upsert,
      opfoelgning: {
        id: this.opfoelgning ? this.opfoelgning.id : 0,
        note: this.note.value,
        deadline: this.deadline.value ? new Date(this.deadline.value).toOkDateString() : null,
        priority: this.prioritet.value,
        entityNo: this.data.entityNo,
      },
      park: this.parker.value,
    };
    this.dialog.close(response);
  }

  remove() {
    const response: OpfoelgningDialogResponse = {
      action: OpfoelgningAction.Delete,
      opfoelgning: {
        id: this.opfoelgning.id,
        entityNo: this.data.entityNo,
      },
    };
    this.dialog.close(response);
  }

  parkerinfo(event: Event) {
    event.stopPropagation();
    this.infoDialog.open(ParkerInfoDialogComponent);
  }

  onSelect(opfoelgning: OpfoelgningDto) {
    this.opfoelgning = opfoelgning;
    this.init();
  }

  onClose() {
    if (!this.opfoelgninger || this.opfoelgninger?.length == 1) this.dialog.close(null);
    else this.opfoelgning = undefined;
  }

  onCreate() {
    this.opfoelgning = {};
    this.init();
  }

  coin_color(priority: number) {
    switch (priority) {
      case 1:
        return 'red';
      case 2:
        return 'orange';
      case 3:
        return 'grey';
    }
  }

  canEdit(opfoelgning: OpfoelgningDto) {
    return opfoelgning.user === this.auth.username;
  }
}
